import {format} from 'date-fns'
import {Label, Radio, RadioGroup as RacRadioGroup} from 'react-aria-components'

import AeroplaneSvg from '@/assets/flight/icons/aeroplane.svg?react'
import Price from '@/components/Price'
import useFlightDetailsQuery from '@/lib/flight/hooks/useFlightDetails'
import {FlightDetailsRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useSearch from '@/utils/router/useSearch'
import style from '@/views/Flight/FlightDetails.module.scss'

import AssistanceForm from './AssistanceForm'

export default memo(function FlightDetailsStep1And2() {
  const {flightId} = FlightDetailsRoute.useParams()
  const [option, setOption] = useSearch(FlightDetailsRoute.id, 'option')
  const {data: flightDetails} = useFlightDetailsQuery(flightId)

  if (!flightDetails) return null

  return (
    <>
      <div className={style.Step}>
        <div className={style.StepTitle} role='heading' aria-level={2}>
          Step 1: Select an option
        </div>
      </div>
      <div>
        <RacRadioGroup
          className={style.OptionGroup}
          value={String(option)}
          onChange={option => {
            void setOption(Number(option))
          }}
          aria-label='Flight option'
        >
          <VisuallyHidden as={Label}>Flight option</VisuallyHidden>
          {flightDetails.booking_options.map((option, index) => {
            const extensions = (() => {
              const extensions: string[] = []

              if (option.together?.extensions) {
                option.together.extensions.forEach(baggage => {
                  extensions.push(baggage)
                })
              }

              if (option.departing?.extensions) {
                option.departing.extensions.forEach(baggage => {
                  extensions.push(baggage)
                })
              }

              if (option.returning?.extensions) {
                option.returning.extensions.forEach(baggage => {
                  extensions.push(baggage)
                })
              }
              return extensions
            })()

            const baggage_prices = (() => {
              const baggage_prices: string[] = []

              if (option.together?.baggage_prices) {
                option.together.baggage_prices.forEach(baggage => {
                  baggage_prices.push(baggage)
                })
              }

              if (option.departing?.baggage_prices) {
                option.departing.baggage_prices.forEach(baggage => {
                  baggage_prices.push(baggage)
                })
              }

              if (option.returning?.baggage_prices) {
                option.returning.baggage_prices.forEach(baggage => {
                  baggage_prices.push(baggage)
                })
              }
              return baggage_prices
            })()
            return (
              <Radio key={index} value={index.toString()} className={style.Option}>
                <div className={style.OptionHeader}>
                  <div className={style.OptionName}>
                    <VisuallyHidden>Option name: </VisuallyHidden>
                    {option.option_title ?? `Option ${index + 1}`}
                  </div>
                  <div className={style.Price}>
                    <VisuallyHidden>Price: </VisuallyHidden>
                    <Price
                      price={option.together?.price}
                      options={{
                        fractionDigits: 2,
                      }}
                    />
                  </div>
                </div>
                {extensions.length > 0 && (
                  <ul className={style.OptionContent} aria-label='Extension'>
                    {extensions.map(extension => (
                      <li key={extension}>{extension}</li>
                    ))}
                  </ul>
                )}
                {extensions.length > 0 && baggage_prices.length > 0 && (
                  <div className={style.Divider} />
                )}
                {baggage_prices.length > 0 && (
                  <ul className={style.OptionContent} aria-label='Baggage price'>
                    {baggage_prices.map(baggage_price => (
                      <li key={baggage_price}>{baggage_price}</li>
                    ))}
                  </ul>
                )}
              </Radio>
            )
          })}
        </RacRadioGroup>
      </div>
      <AssistanceForm />
      <div>
        <div className={style.Step}>
          <div className={style.StepTitle} role='heading' aria-level={2}>
            Step 2: Check the Itinerary detail
          </div>
          <div className={style.StepDescription}>
            You can modify your itinerary by returning to the previous page and selecting different
            options
          </div>
        </div>
        <ol className={style.Itinerary}>
          {flightDetails.selected_flights.map((item, idx) => {
            const firstFlight = item.flights[0]
            const timeline: (Destination | ChangePlane | Plane | string)[] = []

            interface Destination {
              type: 'destination'
              city: string
              airport: string
              time: Date
              end: boolean
            }
            interface ChangePlane {
              type: 'change_plane'
              content: string
              duration?: number | undefined
            }

            interface Plane {
              type: 'plane'
              airline: string
              airline_logo: string
              aircraft: string
              flight_number: string
              number: string
            }

            let numOfLayovers = 0

            item.flights.forEach(flight => {
              if (timeline.length > 0) {
                timeline.push({
                  type: 'change_plane',
                  duration: item.layovers?.[numOfLayovers++]?.duration,
                  content: `Change plane in ${flight.departure_airport.city} (${flight.departure_airport.id})`,
                })
              }

              timeline.push({
                type: 'plane',
                airline: flight.airline,
                airline_logo: flight.airline_logo,
                aircraft: flight.airplane ?? '',
                flight_number: flight.flight_number,
                number: flight.flight_number,
              })

              timeline.push({
                type: 'destination',
                time: new Date(flight.departure_airport.time),
                airport: flight.departure_airport.id,
                city: flight.departure_airport.city,
                end: false,
              })

              timeline.push(`${Math.floor(flight.duration / 60)}h ${flight.duration % 60}m`)

              timeline.push({
                type: 'destination',
                time: new Date(flight.arrival_airport.time),
                airport: flight.arrival_airport.id,
                city: flight.arrival_airport.city,
                end: true,
              })
            })

            const durationText = `${Math.floor(item.total_duration / 60)}h ${item.total_duration % 60}m`

            return (
              <li key={idx} className={style.ItineraryItem}>
                <div className={style.ItineraryHeader}>
                  <div className={style.Identifier}>
                    {format(new Date(firstFlight?.departure_airport.time ?? 0), 'MMM dd, yyyy')}
                  </div>
                  <div className={style.Duration}>{durationText}</div>
                </div>
                <ol className={style.FlightLegs}>
                  {timeline.map((flight, idx) => {
                    if (typeof flight === 'string') {
                      return (
                        <li key={idx} className={style.Duration}>
                          <AeroplaneSvg aria-hidden />
                          <VisuallyHidden>Duration:</VisuallyHidden>
                          {flight}
                        </li>
                      )
                    }

                    if (flight.type === 'plane') {
                      return (
                        <li key={idx} className={style.Plane}>
                          <img src={flight.airline_logo} alt='' />
                          <VisuallyHidden>Airline: </VisuallyHidden>
                          {flight.airline}
                          <VisuallyHidden>Flight number: </VisuallyHidden>({flight.number})
                          {flight.aircraft && (
                            <div className={style.Aircraft}>
                              <VisuallyHidden>Aircraft: </VisuallyHidden>
                              {flight.aircraft}
                            </div>
                          )}
                        </li>
                      )
                    }

                    if (flight.type === 'change_plane') {
                      return (
                        <li key={idx} className={style.ChangePlane}>
                          <AeroplaneSvg aria-hidden />
                          {flight.content}
                          {flight.duration && (
                            <div className={style.ChangePlaneDuration}>
                              <VisuallyHidden>Duration:</VisuallyHidden>
                              {`${Math.floor(flight.duration / 60)}h ${flight.duration % 60}m`}
                            </div>
                          )}
                        </li>
                      )
                    }

                    return (
                      <li key={idx} className={clsx(style.FlightLeg, flight.end && style.End)}>
                        <div className={style.FlightLegTime}>
                          <VisuallyHidden>Time:</VisuallyHidden>
                          {format(new Date(flight.time), 'hh:mm a')} (
                          {format(new Date(flight.time), 'MMM dd, yyyy')})
                        </div>
                        <div className={style.FlightLegPlace}>
                          <VisuallyHidden>City: </VisuallyHidden>
                          {flight.city} (<VisuallyHidden>Airport: </VisuallyHidden>
                          {flight.airport})
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </li>
            )
          })}
        </ol>
      </div>
    </>
  )
})
